import type { SessionUser } from '@/auth/session';
import { getCachedUser } from '@/auth/session';
import { Button, Money } from '@/components/ui';
import { Icon } from '@/components/icons';
import { MetricCard, MetricGrid, ShortcutGrid } from '@/components/DataList';
import { ScreenFrame } from '@/components/Screen';
import type { AdminScreenKey } from '@/nav/keys';

export function DashboardScreen({
  onNavigate,
  user,
}: {
  onNavigate?: (key: AdminScreenKey) => void;
  user?: SessionUser | null;
}) {
  const me = user ?? getCachedUser();
  const go = (key: AdminScreenKey) => onNavigate?.(key);

  return (
    <ScreenFrame
      mode="admin"
      hero
      icon="shield"
      className="admin-dashboard"
      title="لوحة قيادة اشحنلي"
      subtitle="إدارة شاملة للطلبات، المنتجات، المحافظ، المستخدمين، وعمليات USSD من لوحة واحدة."
      actions={
        <>
          <Button type="button" onClick={() => go('products')}>
            <Icon name="package" size={18} /> إدارة المنتجات
          </Button>
          <Button type="button" variant="secondary" onClick={() => go('units')}>
            <Icon name="chart" size={18} /> متابعة الطلبات
          </Button>
        </>
      }
    >
      <MetricGrid>
        <MetricCard
          title="محفظة الوحدات"
          value={<Money value={me?.wallet_balance} />}
          hint="بالليرة السورية"
          icon={<Icon name="wallet" size={21} />}
        />
        <MetricCard
          title="محفظة الألعاب"
          value={<Money value={me?.game_wallet_balance ?? me?.games_wallet_balance ?? 0} suffix="$" />}
          hint="بالدولار"
          icon={<Icon name="game" size={21} />}
        />
        <MetricCard
          title="USSD و API"
          value="مباشر"
          hint="إدارة مستقلة"
          icon={<Icon name="device" size={21} />}
        />
        <MetricCard
          title="المنتجات"
          value="يدوي + API"
          hint="إدارة كاملة"
          icon={<Icon name="package" size={21} />}
        />
      </MetricGrid>

      <ShortcutGrid
        items={[
          {
            key: 'products',
            title: 'إدارة المنتجات',
            text: 'إدارة المنتجات اليدوية ومنتجات API.',
            icon: <Icon name="game" size={24} />,
            onClick: () => go('products'),
          },
          {
            key: 'categories',
            title: 'إدارة الأقسام',
            text: 'إدارة الصور والحالة والترتيب وربط المنتجات.',
            icon: <Icon name="orders" size={24} />,
            onClick: () => go('categories'),
          },
          {
            key: 'units',
            title: 'طلبات الوحدات',
            text: 'متابعة طلبات USSD و API Syria.',
            icon: <Icon name="deposit" size={24} />,
            onClick: () => go('units'),
          },
          {
            key: 'payments',
            title: 'طرق الدفع',
            text: 'إدارة طرق دفع المحافظ.',
            icon: <Icon name="wallet" size={24} />,
            onClick: () => go('payments'),
          },
          {
            key: 'users',
            title: 'المستخدمون والمحافظ',
            text: 'إدارة الزبائن والموزعين والمحافظ.',
            icon: <Icon name="users" size={24} />,
            onClick: () => go('users'),
          },
          {
            key: 'executor',
            title: 'منفذ USSD',
            text: 'متابعة أجهزة التنفيذ والشرائح المتصلة.',
            icon: <Icon name="device" size={24} />,
            onClick: () => go('executor'),
          },
        ]}
      />
    </ScreenFrame>
  );
}
